import {
  APPLICATION_OUTCOME,
  APPLICATION_PROPOSAL_STATUS,
  APPLICATION_STATUS,
  MANAGER_WORKFLOW_STEP_ID,
  MANAGER_WORKFLOW_STEP_STATE,
} from '../../../../config/constants'

export const MANAGER_WORKFLOW_STEP_TITLE_KEYS: Record<ManagerWorkflowStepId, string> = {
  preparation: 'applications__text__mgrStep_preparation',
  executor: 'applications__text__mgrStep_executor',
  client_package: 'applications__text__mgrStep_clientPackage',
  proposal: 'applications__text__mgrStep_proposal',
  client_decision: 'applications__text__mgrStep_clientDecision',
  contacts: 'applications__text__mgrStep_contacts',
  outcome: 'applications__text__mgrStep_outcome',
}

export function clientRefused(app: ApplicationDetail): boolean {
  return Boolean(app.proposal?.declined_at) || app.outcome === APPLICATION_OUTCOME.CLIENT_REFUSED
}

function proposalStepComplete(app: ApplicationDetail): boolean {
  const proposal = app.proposal
  if (!proposal) {
    return false
  }
  if (proposal.status === APPLICATION_PROPOSAL_STATUS.PUBLISHED || proposal.status === APPLICATION_PROPOSAL_STATUS.DECLINED) {
    return true
  }
  if (proposal.published_at || proposal.declined_at) {
    return true
  }
  return false
}

export function clientAcceptedProposal(app: ApplicationDetail): boolean {
  if (clientRefused(app)) {
    return false
  }
  if (app.contact_exchange_requested_at) {
    return true
  }
  return new Set<string>([ APPLICATION_STATUS.AWAITING_EXECUTOR_CONTACT, APPLICATION_STATUS.AT_EXECUTOR ]).has(app.status)
}

const STEP_DEFS: StepDef[] = [
  {
    id: MANAGER_WORKFLOW_STEP_ID.PREPARATION,
    isComplete: (app) => Boolean(app.preparation_completed_at)
      || !new Set<string>([ APPLICATION_STATUS.PREPARING, APPLICATION_STATUS.AWAITING_CLIENT ]).has(app.status),
  },
  {
    id: MANAGER_WORKFLOW_STEP_ID.EXECUTOR,
    isComplete: (app) => Boolean(app.executor_company_id),
  },
  {
    id: MANAGER_WORKFLOW_STEP_ID.CLIENT_PACKAGE,
    isComplete: (app) => Boolean(app.executor_transfer_requested_at),
  },
  {
    id: MANAGER_WORKFLOW_STEP_ID.PROPOSAL,
    isComplete: (app) => proposalStepComplete(app),
  },
  {
    id: MANAGER_WORKFLOW_STEP_ID.CLIENT_DECISION,
    isComplete: (app) => clientAcceptedProposal(app) || clientRefused(app),
  },
  {
    id: MANAGER_WORKFLOW_STEP_ID.CONTACTS,
    isComplete: (app) => Boolean(app.contacts_revealed_at),
    isSkipped: (app) => clientRefused(app),
  },
  {
    id: MANAGER_WORKFLOW_STEP_ID.OUTCOME,
    isComplete: (app) => app.status === APPLICATION_STATUS.CLOSED && app.outcome !== APPLICATION_OUTCOME.UNKNOWN,
  },
]

export function buildManagerWorkflowSteps(app: ApplicationDetail): ManagerWorkflowStepView[] {
  const completed = STEP_DEFS.map((def) => def.isComplete(app))
  const skipped = STEP_DEFS.map((def) => def.isSkipped?.(app) ?? false)

  let currentIndex = STEP_DEFS.findIndex((_, i) => !completed[i] && !skipped[i])
  if (currentIndex === -1) {
    currentIndex = STEP_DEFS.length - 1
  }

  return STEP_DEFS.map((def, i) => {
    let state: ManagerWorkflowStepState
    if (skipped[i]) {
      state = MANAGER_WORKFLOW_STEP_STATE.SKIPPED
    } else if (completed[i]) {
      state = MANAGER_WORKFLOW_STEP_STATE.COMPLETED
    } else if (i === currentIndex) {
      state = MANAGER_WORKFLOW_STEP_STATE.CURRENT
    } else {
      state = MANAGER_WORKFLOW_STEP_STATE.UPCOMING
    }

    return {
      id: def.id,
      state,
      index: i + 1,
    }
  })
}

export function getManagerWorkflowStep({ steps, id }: ApplicationGetManagerWorkflowStepArgs
): ManagerWorkflowStepView | undefined {
  return steps.find((step) => step.id === id)
}

export function managerStepShowsBody(state: ManagerWorkflowStepState): boolean {
  return state === MANAGER_WORKFLOW_STEP_STATE.COMPLETED || state === MANAGER_WORKFLOW_STEP_STATE.CURRENT
}

export function managerWorkflowStepChipColor(
  state: ManagerWorkflowStepState,
): 'success' | 'primary' | 'default' {
  if (state === MANAGER_WORKFLOW_STEP_STATE.COMPLETED) {
    return 'success'
  }
  if (state === MANAGER_WORKFLOW_STEP_STATE.CURRENT) {
    return 'primary'
  }
  return 'default'
}

export function managerWorkflowStepChipVariant(
  state: ManagerWorkflowStepState,
): 'filled' | 'outlined' {
  if (state === MANAGER_WORKFLOW_STEP_STATE.CURRENT) {
    return 'filled'
  }
  return 'outlined'
}

export function managerWorkflowStepWaitingHintMarginTop(
  showBody: boolean,
): number {
  if (showBody) {
    return 1
  }
  return 0
}

export function managerWorkflowStepStateLabelKey({ step, app }: ApplicationManagerWorkflowStepStateLabelKeyArgs
): string {
  if (step.id === MANAGER_WORKFLOW_STEP_ID.PROPOSAL && step.state === MANAGER_WORKFLOW_STEP_STATE.COMPLETED) {
    const proposal = app.proposal
    if (proposal?.declined_at || proposal?.status === APPLICATION_PROPOSAL_STATUS.DECLINED) {
      return 'applications__text__proposalDeclinedByClient'
    }
    if (
      proposal?.status === APPLICATION_PROPOSAL_STATUS.PUBLISHED
      || proposal?.published_at
    ) {
      return 'applications__text__proposalPublishedChip'
    }
  }
  if (step.id === MANAGER_WORKFLOW_STEP_ID.CLIENT_DECISION && step.state === MANAGER_WORKFLOW_STEP_STATE.COMPLETED) {
    if (clientRefused(app)) {
      return 'applications__text__mgrStep_clientDecisionRefused'
    }
    if (clientAcceptedProposal(app)) {
      return 'applications__text__mgrStep_clientDecisionAccepted'
    }
  }
  return `applications__text__mgrStep_state_${step.state}`
}
